import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { format, startOfMonth, subDays, subMonths, endOfMonth } from "date-fns";
import Admin from "../layout/Admin";
import spinner from "../layout/spinner.gif";
import { search, refund } from "../../actions/log";
import { getPrinters } from "../../actions/printer";
import { canAccess } from "../../utils/access";
import { money, dateText } from "../printError/printErrorLabels";
import {
  PAYMENT_TYPES,
  paymentTypeLabel,
  printLabel,
  isMono,
  isPhoto,
  isPaid,
  isPartialRefund,
  refundedAmount,
  totalPages,
  badgeOf,
} from "./logLabels";

// The backend searches one payment type over whole days; the kiosk, status and
// invoice filters work on what came back.

const dayOf = (date) => format(date, "yyyy-MM-dd");

const RANGES = [
  { key: "today", label: "Өнөөдөр", range: (now) => [now, now] },
  { key: "7days", label: "7 хоног", range: (now) => [subDays(now, 6), now] },
  { key: "month", label: "Энэ сар", range: (now) => [startOfMonth(now), now] },
  {
    key: "lastMonth",
    label: "Өмнөх сар",
    range: (now) => [startOfMonth(subMonths(now, 1)), endOfMonth(subMonths(now, 1))],
  },
];
const DEFAULT_RANGE = "today";

// Paid and refunded rows are the payments; the unpaid tab leaves out invoices
// that were paid later, since their paid row is in the list
const TABS = [
  { key: "paid", label: "Төлөгдсөн", test: (item) => isPaid(item) },
  { key: "refunded", label: "Буцаасан", test: (item) => isPaid(item) && item.refunded },
  {
    key: "unpaid",
    label: "Төлөгдөөгүй",
    test: (item, paidInvoices) =>
      !isPaid(item) && !(item.invoice && paidInvoices.has(item.invoice)),
  },
  { key: "all", label: "Бүгд", test: () => true },
];

const fieldClass =
  "block w-full h-10 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:border-purple-400 focus:ring focus:ring-purple-200 focus:ring-opacity-50 focus:outline-none dark:focus:ring-gray-500";
const labelClass =
  "block mb-1 text-xs font-medium tracking-wide text-gray-500 uppercase dark:text-gray-400";
const card = "bg-white rounded-lg shadow-md dark:bg-gray-800";

const count = (value) => Number(value || 0).toLocaleString();

const StatCard = ({ title, value, note }) => (
  <div className={"p-5 " + card}>
    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
    <p className="mt-2 text-2xl font-semibold tracking-tight text-gray-800 truncate dark:text-gray-100">
      {value}
    </p>
    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{note}</p>
  </div>
);

const Spin = ({ className }) => (
  <svg className={"animate-spin " + className} aria-hidden="true" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
  </svg>
);

const Log = ({ search, refund, getPrinters, user, logs, loading, printers }) => {
  const [query, setQuery] = useState(() => {
    const [start, end] = RANGES.find((item) => item.key === DEFAULT_RANGE)
      .range(new Date())
      .map(dayOf);
    return { type: PAYMENT_TYPES[0].value, startDate: start, endDate: end };
  });
  const [range, setRange] = useState(DEFAULT_RANGE);
  // What the list on screen was searched with
  const [loadedWith, setLoadedWith] = useState(query);
  const [tab, setTab] = useState("paid");
  const [printerId, setPrinterId] = useState("");
  const [term, setTerm] = useState("");
  const [refundingId, setRefundingId] = useState(null);

  const load = (next) => {
    setQuery(next);
    setLoadedWith(next);
    search(next);
  };

  // Kiosk names come from the Printer page's list, which needs its own access
  const canSeePrinters = canAccess(user, "printer");

  useEffect(() => {
    load(query);
    if (canSeePrinters && printers.length === 0) getPrinters();
    // Only the initial search runs by itself; later ones go through the filters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickType = (type) => {
    if (!loading && type !== query.type) load({ ...query, type });
  };

  const pickRange = (option) => {
    if (loading) return;
    const [startDate, endDate] = option.range(new Date()).map(dayOf);
    setRange(option.key);
    load({ ...query, startDate, endDate });
  };

  const onDateChange = (e) => {
    setRange(null);
    setQuery({ ...query, [e.target.name]: e.target.value });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!loading && query.startDate && query.endDate) load(query);
  };

  const onRefund = async (item) => {
    const message = item.refunded
      ? `${item.invoice} гүйлгээний буцаалтыг цуцлах уу?\n\nТөлбөр буцаагаагүй гэж тэмдэглэгдэж, холбоотой хэвлэлтийн алдаа дахин хүлээгдэж буй болно.`
      : `${item.invoice} гүйлгээний ${money(item.amount)}-г бүтнээр нь буцаасан гэж тэмдэглэх үү?\n\nХолбоотой хүлээгдэж буй хэвлэлтийн алдаа мөн шийдвэрлэгдэнэ.`;
    if (!window.confirm(message)) return;
    setRefundingId(item._id);
    await refund(item.invoice);
    setRefundingId(null);
  };

  const printerById = useMemo(
    () => new Map(printers.map((printer) => [printer._id, printer])),
    [printers]
  );
  const paidInvoices = useMemo(
    () => new Set(logs.filter(isPaid).map((item) => item.invoice).filter(Boolean)),
    [logs]
  );
  const newestFirst = useMemo(
    () => [...logs].sort((a, b) => new Date(b.date) - new Date(a.date)),
    [logs]
  );

  const inKiosk = printerId
    ? newestFirst.filter((item) => item.printerId === printerId)
    : newestFirst;
  const needle = term.trim().toLowerCase();
  const matches = needle
    ? inKiosk.filter((item) => (item.invoice || "").toLowerCase().includes(needle))
    : inKiosk;
  const activeTab = TABS.find((item) => item.key === tab);
  const rows = matches.filter((item) => activeTab.test(item, paidInvoices));

  // The totals follow the kiosk filter but not the invoice search
  const paid = inKiosk.filter(isPaid);
  const refunded = paid.filter((item) => item.refunded);
  const income = paid.reduce((sum, item) => sum + (item.amount || 0), 0);
  const refundTotal = refunded.reduce((sum, item) => sum + refundedAmount(item), 0);
  const pagesOf = (list) => list.reduce((sum, item) => sum + totalPages(item), 0);
  const photoPages = pagesOf(paid.filter(isPhoto));
  const monoPages = pagesOf(
    paid.filter((item) => !isPhoto(item) && isMono(item.color_type))
  );

  const searchedRange =
    loadedWith.startDate === loadedWith.endDate
      ? loadedWith.startDate
      : `${loadedWith.startDate} — ${loadedWith.endDate}`;

  const tableRows = rows.map((item) => {
    const [day, time] = dateText(item.date).split(" ");
    const printer = printerById.get(item.printerId);
    const badge = badgeOf(item, paidInvoices);
    const refunding = refundingId === item._id;
    return (
      <tr key={item._id} className="text-gray-700 dark:text-gray-400">
        <td className="px-4 py-3 text-sm">
          <p>{day}</p>
          <p className="text-xs text-gray-500 dark:text-gray-500">{time}</p>
        </td>
        <td className="px-4 py-3 text-sm">
          {printer ? (
            <>
              <p className="font-semibold">{printer.printerName}</p>
              <p className="text-xs text-gray-500 dark:text-gray-500">{printer.location || ""}</p>
            </>
          ) : (
            <p className="font-mono text-xs">{item.printerId}</p>
          )}
        </td>
        <td className="px-4 py-3 text-sm">
          <p className="font-mono text-xs">{item.invoice || "—"}</p>
          <p className="text-xs text-gray-500 dark:text-gray-500">
            {paymentTypeLabel(item.payment_type)}
          </p>
        </td>
        <td className="px-4 py-3 text-sm">
          <p>{count(totalPages(item))} хуудас</p>
          <p className="text-xs text-gray-500 dark:text-gray-500">
            {printLabel(item)}
            {item.copies > 1 ? ` · ${item.pages} × ${item.copies} хувь` : ""}
          </p>
        </td>
        <td className="px-4 py-3 text-sm text-right tabular-nums">
          <p className="font-semibold">{money(item.amount)}</p>
          {isPartialRefund(item) && (
            <p className="text-xs text-gray-500 dark:text-gray-500">
              Буцаасан: {money(item.refund_amount)}
            </p>
          )}
        </td>
        <td className="px-4 py-3 text-sm">
          <span
            className={
              "inline-block px-2 py-1 text-xs font-semibold leading-tight rounded-full " +
              badge.badge
            }
          >
            {badge.label}
          </span>
          {item.refunded && item.refunded_date && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
              {dateText(item.refunded_date)}
            </p>
          )}
        </td>
        <td className="px-4 py-3 text-sm text-right">
          {/* The backend refunds or cancels the whole of a paid payment, found by its invoice */}
          {isPaid(item) && item.invoice && (
            <button
              type="button"
              onClick={() => onRefund(item)}
              disabled={refunding}
              className={`inline-flex items-center px-3 py-1 text-sm font-medium leading-5 transition-colors duration-150 border rounded-lg focus:outline-none focus:ring-2 ${
                item.refunded
                  ? "text-gray-700 bg-white border-gray-300 hover:bg-gray-100 focus:ring-purple-200 dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600"
                  : "text-red-600 bg-white border-red-200 hover:bg-red-50 focus:ring-red-200 dark:text-red-300 dark:bg-gray-800 dark:border-red-700 dark:hover:bg-gray-700"
              } ${refunding ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {refunding && <Spin className="w-4 h-4 mr-2" />}
              {item.refunded ? "Цуцлах" : "Буцаах"}
            </button>
          )}
        </td>
      </tr>
    );
  });

  return (
    <Admin>
      <h2 className="my-6 text-2xl font-semibold text-gray-700 dark:text-gray-200">
        Гүйлгээ
      </h2>

      <form onSubmit={onSubmit} className={"p-4 mb-6 " + card}>
        <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
          <div>
            <span className={labelClass}>Төлбөрийн төрөл</span>
            <div className="inline-flex h-10 p-1 bg-gray-100 rounded-lg dark:bg-gray-700">
              {PAYMENT_TYPES.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  onClick={() => pickType(option.value)}
                  aria-pressed={query.type === option.value}
                  className={`px-4 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-purple-300 ${
                    query.type === option.value
                      ? "text-purple-700 bg-white shadow-sm dark:text-purple-300 dark:bg-gray-800"
                      : "text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className={labelClass}>Хугацаа</span>
            <div className="flex flex-wrap gap-2">
              {RANGES.map((option) => (
                <button
                  type="button"
                  key={option.key}
                  onClick={() => pickRange(option)}
                  aria-pressed={range === option.key}
                  className={`h-10 px-4 text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-purple-300 ${
                    range === option.key
                      ? "text-white bg-purple-600 shadow-sm"
                      : "text-gray-600 bg-gray-100 hover:bg-gray-200 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-3 xl:ml-auto">
            <div>
              <span className={labelClass}>Огноо</span>
              <div className="flex items-center">
                <input
                  type="date"
                  name="startDate"
                  value={query.startDate}
                  max={query.endDate || undefined}
                  onChange={onDateChange}
                  aria-label="Эхлэх огноо"
                  className={fieldClass + " min-w-0 px-3"}
                />
                <span className="mx-2 text-gray-400 dark:text-gray-500">—</span>
                <input
                  type="date"
                  name="endDate"
                  value={query.endDate}
                  min={query.startDate || undefined}
                  onChange={onDateChange}
                  aria-label="Дуусах огноо"
                  className={fieldClass + " min-w-0 px-3"}
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className={`inline-flex items-center h-10 px-4 text-sm font-medium leading-5 text-white transition-colors duration-150 bg-purple-600 border border-transparent rounded-lg active:bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-200 ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {loading ? (
                <Spin className="w-4 h-4 mr-2" />
              ) : (
                <svg
                  className="w-4 h-4 mr-2"
                  aria-hidden="true"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              )}
              Хайх
            </button>
          </div>
        </div>
      </form>

      {loading && logs.length === 0 ? (
        <img
          src={spinner}
          className="my-6"
          style={{ width: "50px", margin: "auto", display: "block" }}
          alt="Loading..."
        />
      ) : (
        // The last list stays, dimmed, while the next one loads
        <div className={"transition-opacity " + (loading ? "opacity-50" : "")}>
          <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
            {paymentTypeLabel(loadedWith.type)} · {searchedRange}
          </p>

          <div className="grid gap-4 mb-6 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard title="Орлого" value={money(income)} note={`${count(paid.length)} гүйлгээ`} />
            <StatCard
              title="Буцаалт"
              value={money(refundTotal)}
              note={`${count(refunded.length)} гүйлгээ`}
            />
            <StatCard
              title="Цэвэр орлого"
              value={money(income - refundTotal)}
              note="Орлогоос буцаалтыг хассан"
            />
            <StatCard
              title="Төлсөн хуудас"
              value={count(pagesOf(paid))}
              note={
                `Хар цагаан ${count(monoPages)} · Өнгөт ${count(pagesOf(paid) - monoPages - photoPages)}` +
                (photoPages > 0 ? ` · Зураг ${count(photoPages)}` : "")
              }
            />
          </div>

          <div className={"mb-8 overflow-hidden " + card}>
            <div className="flex flex-col gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-700 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap gap-1" role="tablist">
                {TABS.map((option) => {
                  const active = option.key === tab;
                  const size = matches.filter((item) => option.test(item, paidInvoices)).length;
                  return (
                    <button
                      type="button"
                      role="tab"
                      aria-selected={active}
                      key={option.key}
                      onClick={() => setTab(option.key)}
                      className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-purple-300 ${
                        active
                          ? "text-purple-700 bg-purple-100 dark:text-purple-200 dark:bg-gray-700"
                          : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                      }`}
                    >
                      {option.label}
                      <span
                        className={`ml-2 px-2 text-xs font-semibold rounded-full tabular-nums ${
                          active
                            ? "text-purple-700 bg-white dark:text-purple-200 dark:bg-gray-800"
                            : "text-gray-500 bg-gray-100 dark:text-gray-400 dark:bg-gray-700"
                        }`}
                      >
                        {count(size)}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-col gap-2 sm:flex-row">
                {printers.length > 0 && (
                  <select
                    value={printerId}
                    onChange={(e) => setPrinterId(e.target.value)}
                    aria-label="Киоск"
                    className={fieldClass + " pl-3 pr-10 sm:w-56"}
                  >
                    <option value="">Бүх киоск</option>
                    {printers.map((printer) => (
                      <option key={printer._id} value={printer._id}>
                        {printer.printerName}
                        {printer.location ? " - " + printer.location : ""}
                      </option>
                    ))}
                  </select>
                )}
                <div className="relative sm:w-64">
                  <svg
                    className="absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 pointer-events-none left-3 top-1/2"
                    aria-hidden="true"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="search"
                    value={term}
                    onChange={(e) => setTerm(e.target.value)}
                    placeholder="Нэхэмжлэхийн дугаар"
                    aria-label="Нэхэмжлэхээр шүүх"
                    className={fieldClass + " pl-9 pr-3"}
                  />
                </div>
              </div>
            </div>

            {rows.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Гүйлгээ олдсонгүй
                </p>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {logs.length === 0
                    ? "Сонгосон төрөл, хугацаанд гүйлгээ бүртгэгдээгүй байна."
                    : "Шүүлтүүрээ өөрчлөөд үзээрэй."}
                </p>
              </div>
            ) : (
              <div className="w-full overflow-x-auto">
                <table className="w-full whitespace-nowrap">
                  <thead>
                    <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b dark:border-gray-700 bg-gray-50 dark:text-gray-400 dark:bg-gray-800">
                      <th className="px-4 py-3">Огноо</th>
                      <th className="px-4 py-3">Киоск</th>
                      <th className="px-4 py-3">Нэхэмжлэх</th>
                      <th className="px-4 py-3">Хэвлэлт</th>
                      <th className="px-4 py-3 text-right">Дүн</th>
                      <th className="px-4 py-3">Төлөв</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y dark:divide-gray-700 dark:bg-gray-800">
                    {tableRows}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </Admin>
  );
};

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  note: PropTypes.string,
};

Log.propTypes = {
  search: PropTypes.func.isRequired,
  refund: PropTypes.func.isRequired,
  getPrinters: PropTypes.func.isRequired,
  user: PropTypes.object,
  logs: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  printers: PropTypes.array.isRequired,
};

const mapStateToProps = (state) => ({
  user: state.auth.user,
  logs: state.log.logs,
  loading: state.log.loading,
  printers: state.printer.printers,
});

export default connect(mapStateToProps, { search, refund, getPrinters })(Log);
